// ═══════════════════════════════════════════════════════════════════════════
// LE CODE TRAVAILLEUR DmfA — UNE SEULE PORTE
//
// POURQUOI CE FICHIER EXISTE (18/08/2026). Le fondateur envoie le C4 et la
// fiche de paie du MÊME dossier, le même jour. Le C4 porte le code **841**, la
// fiche de paie le code **495**. Deux documents officiels du même travailleur,
// deux codes différents.
//
// Le C4 avait raison : 841 est le code de l'étudiant employé sous cotisation de
// solidarité, et il vient de `lois-belges.js`, sourcé. La fiche de paie, elle,
// écrivait `empData.dmfaCode || '495'` — un défaut en dur, aveugle au régime.
//
// ⚠️ C'EST LA CLASSE DE BUG DÉJÀ CORRIGÉE 22 FOIS EN AOÛT : un régime spécial
// décidé dans son coin par chaque fichier. La réparation n'est pas « mettre 841
// dans la fiche de paie », c'est **une seule porte** que tous interrogent.
//
// 📖 Sources des codes, lues au texte :
//   • **015** ouvriers ordinaires · **495** travailleurs intellectuels —
//     Annexe 2 des instructions ONSS (« Liste des codes travailleurs pour
//     lesquels des cotisations sont dues », version 2024/2), déjà citée et
//     vérifiée dans `cp-coherence.js` (⚠️ **497** = gardiens d'enfants, PAS un
//     ouvrier ordinaire — l'erreur avait été faite le 07/08).
//   • **840** étudiant ouvrier · **841** étudiant employé — instructions ONSS
//     2026/2, bloc 90003 StudentContribution, dans `lois-belges.js`.
//
//   • **050** flexijob manuel · **450** flexijob intellectuel — table de codes
//     officielle ONSS déjà présente dans le dépôt (`ltds-codes.js`, annexes
//     structurées ONSS via le catalogue eGov Smals, extraites le 24/07/2026),
//     qui les libelle « Travailleurs manuels / intellectuels occupés dans le
//     cadre d'un flexijob ». La même table confirme 840 et 841 — deuxième
//     source concordante, indépendante de `lois-belges.js`.
//
// 🔴 CE QU'ON NE SAIT PAS ENCORE. Les autres régimes spéciaux (apprenti,
// article 17, extras horeca, intérim…) ont leurs propres codes travailleurs, et
// aucun n'est sourcé de façon sûre aujourd'hui — la table LTDS en porte
// plusieurs, mais certains libellés y sont tronqués ou en doublon, et un code
// faux part en DmfA. On ne les invente donc pas : on rend le code ordinaire ET
// un drapeau `aVerifier`, pour que l'écran puisse le dire au lieu de laisser
// croire que le code est certain.
// ═══════════════════════════════════════════════════════════════════════════
import { regimeSpecialDe } from '@/app/lib/regimes-speciaux';
import { categoriePourCP } from '@/app/lib/cp-coherence';
import { LB } from '@/app/lib/lois-belges';

/** Le code ordinaire de repli quand la CP ne tranche pas (employé). */
export const CODE_EMPLOYE_ORDINAIRE = '495';

/**
 * Les régimes dont le code travailleur est SOURCÉ, par statut.
 *
 * ⚠️ Chaque code est vérifié contre `ltds-codes.js` par
 * `__tests__/code-travailleur-dmfa.test.js` : si l'ONSS republie sa table et
 * change un libellé, le test tombe. C'est ce qui distingue une valeur sourcée
 * d'une valeur recopiée.
 */
export const CODES_PAR_REGIME = {
  etudiant: { ouvrier: '840', employe: '841', source: 'ONSS instructions 2026/2 — cotisation de solidarité étudiant (840 ouvrier / 841 employé), confirmé par la table LTDS' },
  flexijob: { ouvrier: '050', employe: '450', source: 'Table de codes officielle ONSS (annexes structurées, extraites le 24/07/2026) — 050 travailleurs manuels / 450 travailleurs intellectuels occupés dans le cadre d’un flexijob' },
};

const norm = (v) => String(v ?? '').trim().toLowerCase();

/** Quel régime un code déjà stocké désigne-t-il ? `null` s'il est ordinaire. */
export function regimeDuCode(code) {
  const c = String(code ?? '').trim();
  if (!c) return null;
  for (const [regime, e] of Object.entries(CODES_PAR_REGIME)) {
    if (c === e.ouvrier || c === e.employe) return regime;
  }
  return null;
}

/** Ouvrier ou employé ? La CP tranche quand elle est univoque, sinon la fiche. */
function statutDe(emp, cp) {
  const parCP = categoriePourCP(cp || emp?.cp || emp?.data?.cp);
  if (parCP) return parCP.statut;
  const s = norm(emp?.statut || emp?.data?.statut || emp?.statutSocial);
  if (s === 'ouvrier' || s === 'employe' || s === 'employé') return s === 'ouvrier' ? 'ouvrier' : 'employe';
  return null;
}

/**
 * Le code travailleur DmfA d'une fiche.
 *
 * @param {object} emp la fiche du travailleur
 * @param {{cp?:string}} opts la CP à utiliser si la fiche ne la porte pas
 * @returns {{code:string, regime:string|null, statut:string|null,
 *            aVerifier:boolean, source:string}}
 */
export function codeTravailleurDmfa(emp, { cp } = {}) {
  // ⚠️ UN CODE SPÉCIAL DÉJÀ EN BASE EST LUI-MÊME UN MARQUEUR DE RÉGIME. Des
  // fiches anciennes ne portent le régime NULLE PART ailleurs que dans ce code
  // (importées, ou saisies avant que le statut existe). Le re-dériver des seuls
  // champs de statut les requalifierait en salariés ordinaires — un étudiant
  // repasserait à l'ONSS pleine au lieu de la solidarité à 8,13 %.
  const regime = regimeSpecialDe(emp) || regimeDuCode(emp?.dmfaCode || emp?.data?.dmfaCode);
  const statut = statutDe(emp, cp);
  const parCP = categoriePourCP(cp || emp?.cp || emp?.data?.cp);
  const ordinaire = parCP?.dmfaCode
    || (statut === 'ouvrier' ? '015' : null)
    || String(emp?.dmfaCode || emp?.data?.dmfaCode || '').trim()
    || CODE_EMPLOYE_ORDINAIRE;

  const sourcé = CODES_PAR_REGIME[regime];
  if (sourcé) {
    // L'étudiant garde `lois-belges.js` comme source première — c'est là que
    // vivent ses taux, et les deux doivent rester d'accord.
    const etu = LB?.cotisations?.etudiant || {};
    const depuisLois = regime === 'etudiant'
      ? (statut === 'ouvrier' ? etu.codeDmfaOuvrier : etu.codeDmfaEmploye) : null;
    const code = depuisLois || (statut === 'ouvrier' ? sourcé.ouvrier : sourcé.employe);
    if (code) {
      return {
        code: String(code), regime, statut: statut || 'employe', aVerifier: false, source: sourcé.source,
      };
    }
  }

  return {
    code: String(ordinaire),
    regime,
    statut,
    // Un régime spécial sans code sourcé : on rend l'ordinaire, mais on le DIT.
    aVerifier: !!regime && !CODES_PAR_REGIME[regime],
    source: regime && !CODES_PAR_REGIME[regime]
      ? `régime « ${regime} » — code travailleur propre NON sourcé dans le dépôt, code ordinaire rendu par défaut`
      : 'Annexe 2 des instructions ONSS 2024/2 — 015 ouvriers ordinaires / 495 travailleurs intellectuels',
  };
}
